import { useLingui } from '@lingui/react/macro';
import { useEffect } from 'preact/hooks';
import { useLocation, useNavigate } from 'react-router-dom';
import { subscribe, useSnapshot } from 'valtio';

import Accounts from '../pages/accounts';
import Settings from '../pages/settings';
import { useAuth } from '../utils/auth-context';
import focusDeck from '../utils/focus-deck';
import showToast from '../utils/show-toast';
import states from '../utils/states';

import AccountSheet from './account-sheet';
import ComposeSuspense, { preload } from './compose-suspense';
import Drafts from './drafts';
import EmbedModal from './embed-modal';
import GenericAccounts from './generic-accounts';
import ImportExportAccounts from './import-export-accounts';
import MediaAltModal from './media-alt-modal';
import MediaModal from './media-modal';
import MediaRevealSheet from './media-reveal-sheet';
import Modal from './modal';
import OpenLinkSheet from './open-link-sheet';
import QrCodeModal from './qr-code-modal';
import QrScannerModal from './qr-scanner-modal';
import ReportModal from './report-modal';
import ShortcutsSettings from './shortcuts-settings';

subscribe(states, (changes) => {
  for (const [action, path, value, prevValue] of changes) {
    // When closing modal, focus on deck
    if (/^show/i.test(path) && !value) {
      focusDeck();
    }
  }
});

export default function Modals() {
  const { t } = useLingui();
  const snapStates = useSnapshot(states);
  const navigate = useNavigate();
  const location = useLocation();
  const isLoggedIn = useAuth();

  useEffect(() => {
    setTimeout(preload, 1000);
  }, []);

  return (
    <>
      {isLoggedIn && !!snapStates.showCompose && (
        <Modal
          class={`solid ${snapStates.composerState.minimized ? 'min' : ''}`}
          minimized={!!snapStates.composerState.minimized}
        >
          <ComposeSuspense
            replyToStatus={
              typeof snapStates.showCompose !== 'boolean'
                ? snapStates.showCompose.replyToStatus
                : window.__COMPOSE__?.replyToStatus || null
            }
            replyMode={
              states.showCompose?.replyMode ||
              window.__COMPOSE__?.replyMode ||
              'all'
            }
            editStatus={
              states.showCompose?.editStatus ||
              window.__COMPOSE__?.editStatus ||
              null
            }
            draftStatus={
              states.showCompose?.draftStatus ||
              window.__COMPOSE__?.draftStatus ||
              null
            }
            quoteStatus={
              states.showCompose?.quoteStatus ||
              window.__COMPOSE__?.quoteStatus ||
              null
            }
            sharedData={window.__SHARED_DATA__ || null}
            onClose={(results) => {
              const { newStatus, instance, type, scheduledAt } = results || {};
              states.showCompose = false;
              window.__COMPOSE__ = null;
              window.__SHARED_DATA__ = null;
              if (newStatus) {
                states.reloadStatusPage++;
                if (scheduledAt) states.reloadScheduledPosts++;
                showToast({
                  text: {
                    post: scheduledAt
                      ? t`Post scheduled`
                      : t`Post published. Check it out.`,
                    reply: scheduledAt
                      ? t`Reply scheduled`
                      : t`Reply posted. Check it out.`,
                    edit: t`Post updated. Check it out.`,
                  }[type || 'post'],
                  delay: 1000,
                  duration: 10_000, // 10 seconds
                  onClick: (toast) => {
                    toast.hideToast();
                    states.prevLocation = location;
                    if (scheduledAt) {
                      navigate('/sp');
                    } else {
                      navigate(
                        instance
                          ? `/${instance}/s/${newStatus.id}`
                          : `/s/${newStatus.id}`,
                      );
                    }
                  },
                });
              }
            }}
          />
        </Modal>
      )}
      {isLoggedIn && !!snapStates.showSettings && (
        <Modal
          onClose={() => {
            states.showSettings = false;
          }}
        >
          <Settings
            onClose={() => {
              states.showSettings = false;
            }}
          />
        </Modal>
      )}
      {isLoggedIn && !!snapStates.showAccounts && (
        <Modal
          onClose={() => {
            states.showAccounts = false;
          }}
        >
          <Accounts
            onClose={() => {
              states.showAccounts = false;
            }}
          />
        </Modal>
      )}
      {!!snapStates.showAccount && (
        <Modal
          onClose={() => {
            states.showAccount = false;
          }}
        >
          <AccountSheet
            account={snapStates.showAccount?.account || snapStates.showAccount}
            instance={snapStates.showAccount?.instance}
            onClose={({ destination } = {}) => {
              states.showAccount = false;
              // states.showGenericAccounts = false;
              // if (destination) {
              //   states.showAccounts = false;
              // }
            }}
          />
        </Modal>
      )}
      {!!snapStates.showMediaReveal && (
        <Modal
          onClose={() => {
            states.showMediaReveal = false;
          }}
        >
          <MediaRevealSheet
            onClose={() => {
              states.showMediaReveal = false;
            }}
          />
        </Modal>
      )}
      {!!snapStates.showOpenLink && (
        <Modal
          onClose={() => {
            states.showOpenLink = false;
          }}
        >
          <OpenLinkSheet
            url={snapStates.showOpenLink.url}
            linkText={snapStates.showOpenLink.linkText}
            onClose={() => {
              states.showOpenLink = false;
            }}
          />
        </Modal>
      )}
      {isLoggedIn && !!snapStates.showDrafts && (
        <Modal
          onClose={() => {
            states.showDrafts = false;
          }}
        >
          <Drafts onClose={() => (states.showDrafts = false)} />
        </Modal>
      )}
      {!!snapStates.showMediaModal && (
        <Modal
          onClick={(e) => {
            if (
              e.target === e.currentTarget ||
              e.target.classList.contains('media')
            ) {
              states.showMediaModal = false;
            }
          }}
        >
          <MediaModal
            mediaAttachments={snapStates.showMediaModal.mediaAttachments}
            instance={snapStates.showMediaModal.instance}
            index={snapStates.showMediaModal.mediaIndex}
            statusID={snapStates.showMediaModal.statusID}
            onClose={() => {
              states.showMediaModal = false;
            }}
          />
        </Modal>
      )}
      {isLoggedIn && !!snapStates.showShortcutsSettings && (
        <Modal
          onClose={() => {
            states.showShortcutsSettings = false;
          }}
        >
          <ShortcutsSettings
            onClose={() => (states.showShortcutsSettings = false)}
          />
        </Modal>
      )}
      {!!snapStates.showGenericAccounts && (
        <Modal
          onClose={() => {
            states.showGenericAccounts = false;
          }}
        >
          <GenericAccounts
            instance={snapStates.showGenericAccounts.instance}
            excludeRelationshipAttrs={
              snapStates.showGenericAccounts.excludeRelationshipAttrs
            }
            postID={snapStates.showGenericAccounts.postID}
            onClose={() => (states.showGenericAccounts = false)}
            blankCopy={snapStates.showGenericAccounts.blankCopy}
          />
        </Modal>
      )}
      {!!snapStates.showMediaAlt && (
        <Modal
          onClose={(e) => {
            states.showMediaAlt = false;
          }}
        >
          <MediaAltModal
            alt={snapStates.showMediaAlt.alt || snapStates.showMediaAlt}
            lang={snapStates.showMediaAlt?.lang}
            onClose={() => {
              states.showMediaAlt = false;
            }}
          />
        </Modal>
      )}
      {!!snapStates.showEmbedModal && (
        <Modal
          class="solid"
          onClose={() => {
            states.showEmbedModal = false;
          }}
        >
          <EmbedModal
            html={snapStates.showEmbedModal.html}
            url={snapStates.showEmbedModal.url}
            width={snapStates.showEmbedModal.width}
            height={snapStates.showEmbedModal.height}
            onClose={() => {
              states.showEmbedModal = false;
            }}
          />
        </Modal>
      )}
      {isLoggedIn && !!snapStates.showReportModal && (
        <Modal
          onClose={() => {
            states.showReportModal = false;
          }}
        >
          <ReportModal
            account={snapStates.showReportModal.account}
            post={snapStates.showReportModal.post}
            onClose={() => {
              states.showReportModal = false;
            }}
          />
        </Modal>
      )}
      {!!snapStates.showQrCodeModal && (
        <Modal
          class="solid"
          onClose={() => {
            states.showQrCodeModal = false;
          }}
        >
          <QrCodeModal
            text={snapStates.showQrCodeModal.text}
            arena={snapStates.showQrCodeModal.arena}
            backgroundMask={snapStates.showQrCodeModal.backgroundMask}
            caption={snapStates.showQrCodeModal.caption}
            onClose={() => {
              states.showQrCodeModal = false;
            }}
            onScannerClick={snapStates.showQrCodeModal.onScannerClick}
          />
        </Modal>
      )}
      {!!snapStates.showQrScannerModal && (
        <Modal
          class="solid"
          onClose={() => {
            states.showQrScannerModal = false;
          }}
        >
          <QrScannerModal
            checkValidity={snapStates.showQrScannerModal.checkValidity}
            actionableText={snapStates.showQrScannerModal.actionableText}
            onClose={(...args) => {
              if (snapStates.showQrScannerModal.onClose) {
                snapStates.showQrScannerModal.onClose(...args);
              }
              states.showQrScannerModal = false;
            }}
          />
        </Modal>
      )}
      {isLoggedIn && !!snapStates.showImportExportAccounts && (
        <Modal
          onClose={() => {
            states.showImportExportAccounts = false;
          }}
        >
          <ImportExportAccounts
            onClose={() => {
              states.showImportExportAccounts = false;
            }}
            exportDisabled={
              typeof snapStates.showImportExportAccounts === 'object'
                ? snapStates.showImportExportAccounts.exportDisabled
                : false
            }
          />
        </Modal>
      )}
    </>
  );
}
